/**
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2018, 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'underscore',
	'./AbstractTupleLocator',
	'./ViprAreaLocator'
], function (_, AbstractTupleLocator, ViprAreaLocator) {

	class BinarySearchTupleLocator extends AbstractTupleLocator {

		/**
		 * A tuple locator which deals with tuples which the VIDA getItemsInPolygon
		 * API returns.
		 */
		constructor({ viprWidget }) {
			super();
			this.viprWidget = viprWidget;
		}

		/* For JSDocs, see parent class */
		findTupleVisualInfo(filter, { includeInfo, includePoints, includeBounds }) {
			const viprItems = _.flatten(
				//Get all items in an area
				this._getVisAreas().map(({ x, y, w, h }) =>
					this._getVIDAItemsInRectangle({ x0: x, y0: y, x1: x+w, y1: y+h })
					//Filter items to ones which satisfy specified filter
						.filter(item => this._itemSatisfiesFilter({ item, filter }))
				)
			);

			return viprItems.map(
				item => this._buildResult(
					item,
					{ includeInfo, includePoints, includeBounds }
				)
			);
		}

		_buildResult(item, { includeInfo, includePoints, includeBounds }) {
			const result = {};
			const coords = (includePoints || includeBounds) && this._getCoordinates(
				item,
				{
					includeBounds,
					includePoints
				}
			);
			if (includeInfo) {
				result.info = this._getValueInfoFromItem(item);
			}
			if (includePoints) {
				result.point = {
					selectedNode: coords.selectedNode,
					...coords.point
				};
			}
			if (includeBounds) {
				result.bounds = {
					selectedNode: coords.selectedNode,
					...coords.bounds
				};
			}
			return result;
		}

		/*
		 * Finds the areas (e.g. "visualization", "legend") and their extents which
		 * VIPR partitions the entire visualization into.
		 *
		 * Motivation: It is important to know their extents, as if a
		 * getItemsInPolygon query draws a polygon with points straddling multiple
		 * areas, VIPR will return no items.
		 *
		 * @return {object[]} area information will include 'area' (name), 'x', 'y',
		 * 'w' and 'h'
		 */
		_getVisAreas() {
			const areaLocator = new ViprAreaLocator({
				viprWidget: this.viprWidget
			});

			areaLocator.findAreasFromCorners();
			areaLocator.findAreasFromSelector('[data-charttype]');

			// This guards against us not having been able to find the top-level area of a visualization
			// these are the only areas this locator was designed to work with
			const visAreas = areaLocator.getAreasOfType('visualization');
			const layouterAreas = areaLocator.getAreasOfType('layouter');
			if(visAreas.length === 0 && layouterAreas.length === 0) {
				throw new Error('Cannot find top-level (visualization|layouter) area of widget');
			}

			return areaLocator.getVisAreas();
		}

		/*
		 * Simplifies the VIPR getItemsInPolygon API to a rectangle - and takes care
		 * of the mapping to VIPR coordinate space via getVisCoordinate.
		 * @param {object} coords
		 * @param {number} coords.x0 - top-left corner x coordinate
		 * @param {number} coords.y0 - top-left corner y coordinate
		 * @param {number} coords.x1 - bottom-right corner x coordinate
		 * @param {number} coords.y1 - bottom-right corner y coordinate
		 */
		_getVIDAItemsInRectangle({ x0, y0, x1, y1 }) {
			const origin = this.viprWidget.domNode.getBoundingClientRect();

			//Make a 'rectangle' polygon:
			//	- top-left corner
			//	- top-right corner
			//	- bottom-right corner
			//	- bottom-left corner
			const points = [[x0, y0],[x1, y0],[x1, y1],[x0, y1]]
				.map(([x,y]) => ([x + origin.x, y + origin.y]))
				.map(([x,y]) => (this.viprWidget.getVisCoordinate(x, y)));

			try {
				return this.viprWidget.getItemsInPolygon(points);
			} catch(e) {
				// I've seen cases where VIDA throws an error on this call,
				// namely on an area of type 'column'
				console.error(e);
				return [];
			}
		}

		/*
		 * Finds relevant coordinates to the item
		 * @param {object} item
		 * @param {object} options
		 * @param {object} [options.includePoints] if true, the returned object will include a `point` object with
		 *                                         members `xOffset` and `yOffset`, relative to the returned
		 *                                         `selectedNode`. This coordinate is a point on the shape
		 *                                         representing the item which can be clicked.
		 * @param {object} [options.includeBounds] if true, the returned object will include a `bounds` object with
		 *                                         members `leftOffset`, `rightOffset`, `topOffset` and `bottoOffset`,
		 *                                         relative to the returne `selectedNode`. These represent the
		 *                                         bounding rectangle of the shape representing the item.
		 * @returns {object} with members: `selectNode`, a DOM node, `point`, if `includePoints` param is specified, 
		 *                   and `bounds`, if `includeBounds` param is specified
		 */
		_getCoordinates(item, { includePoints, includeBounds }) {
			//Find which area this item is contained in and extract its extents
			const extent = this._getVisAreas().find(
				({ x,y,w,h }) => this._isItemContainedIn(item, { x0: x, y0: y, x1: x+w, y1: y+h })
			);

			if(!extent) {
				throw new Error('Item not found at any position within visualization');
			}

			const areaLeft = extent.x,
				areaTop = extent.y,
				areaRight = areaLeft + extent.w,
				areaBottom = areaTop + extent.h;

			//Find the left-offset of the item
			const itemLeft = this._binarySearch(areaRight, areaLeft, left => this._isItemContainedIn(item, { x0: areaLeft, y0: areaTop, x1: left,   y1: areaBottom }));

			//Find the bounding box of the item
			let bounds = {};
			if (includeBounds) {
				const leftOffset = itemLeft;
				const rightOffset = this._binarySearch(leftOffset, areaRight, right => this._isItemContainedIn(item, { x0: right, y0: areaTop, x1: areaRight, y1: areaBottom }));
				const topOffset = this._binarySearch(areaBottom, areaTop, top => this._isItemContainedIn(item, { x0: areaLeft, y0: areaTop, x1: areaRight, y1: top }));
				const bottomOffset = this._binarySearch(topOffset, areaBottom, bottom => this._isItemContainedIn(item, { x0: areaLeft, y0: bottom, x1: areaRight, y1: areaBottom }));

				bounds = { leftOffset, rightOffset, topOffset, bottomOffset };
			}

			//Find a single clickable point on the item
			//We cannot assume the item is rectangular, or even that the center of its bounding box would actually hit the item.
			//We do know that there must e at least one point along each line of its bounding box, so just find a point along that:
			let point = {};
			if (includePoints) {
				const boundsTop = bounds.topOffset || areaTop;
				const boundsBottom = bounds.bottomOffset || areaBottom;
				const xOffset = itemLeft;
				const yOffset = this._binarySearch(boundsBottom, boundsTop, y => this._isItemContainedIn(item, { x0: xOffset - 1, y0: areaTop, x1: xOffset, y1: y }) );
				point = { xOffset, yOffset };
			}

			return {
				selectedNode: this.viprWidget.domNode,
				bounds,
				point
			};
		}

		/*
		 * Perform a binary search for the boundary between a condition succeeding and failing
		 * @param {number} maxPass - the maximum value where the condition is known to pass
		 * @param {number} maxFail - the maximum value where the condition is known to fail
		 * @param {function} test  - condition
		 */
		_binarySearch(maxPass, maxFail, test) {
			while(Math.abs(maxPass - maxFail) > 1) {
				const candidate = Math.round((maxPass + maxFail) / 2);
				if(test(candidate)) {
					maxPass = candidate;
				} else {
					maxFail = candidate;
				}
			}
			return maxPass;
		}

		/*
		 * Determines whether the item exists inside a particular rectangle in the visualization
		 * @param {object} item
		 * @param {object} extents - rectangle
		 */
		_isItemContainedIn(item, { x0, y0, x1, y1 }) {
			return this._getVIDAItemsInRectangle({ x0, y0, x1, y1 }).some(foundItem => item.key === foundItem.key);
		}

	}

	return BinarySearchTupleLocator;
});
